import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { normalizeScheduleTime, shiftScheduleTime } from '@/lib/publishing/normalizeScheduleTime';
import { cn } from '@/lib/utils';

const HOURS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, minute) => String(minute).padStart(2, '0'));

interface Props {
  id: string;
  testId: string;
  value: string;
  min?: string;
  onChange: (value: string) => void;
}

function scrollSelectedIntoView(scroller: HTMLDivElement | null, selected: HTMLElement | null) {
  if (!scroller || !selected) return;
  const top = selected.offsetTop - scroller.clientHeight / 2 + selected.offsetHeight / 2;
  scroller.scrollTop = Math.max(0, top);
}

export const ScheduleTimeField: React.FC<Props> = ({
  id,
  testId,
  value,
  min,
  onChange,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);
  const hourButtonRef = useRef<HTMLButtonElement>(null);
  const minuteButtonRef = useRef<HTMLButtonElement>(null);
  const committed = normalizeScheduleTime(value) || value;
  const [hour = '00', minute = '00'] = committed.split(':');

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    scrollSelectedIntoView(hourListRef.current, hourButtonRef.current);
    scrollSelectedIntoView(minuteListRef.current, minuteButtonRef.current);
  }, [hour, minute]);

  const commit = (next: string) => {
    const normalized = normalizeScheduleTime(next);
    if (!normalized) {
      setDraft(value);
      return;
    }
    if (min && normalized < min) {
      setDraft(min);
      onChange(min);
      return;
    }
    setDraft(normalized);
    if (normalized !== value) onChange(normalized);
  };

  const applyPart = (nextHour: string, nextMinute: string) => {
    commit(`${nextHour}:${nextMinute}`);
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium" htmlFor={id}>
        {t('publishing.schedule.timeLabel')}
      </label>
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-9 shrink-0"
          data-testid={`${testId}-nudge-down`}
          aria-label={t('publishing.schedule.timeEarlier')}
          onClick={() => commit(shiftScheduleTime(committed || '00:00', -15))}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Input
          id={id}
          data-testid={testId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('publishing.schedule.timePlaceholder')}
          value={draft}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            const normalized = normalizeScheduleTime(next);
            if (normalized && (!min || normalized >= min)) onChange(normalized);
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(draft);
            }
          }}
          className="h-10 text-center font-mono text-base tabular-nums tracking-wide"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-9 shrink-0"
          data-testid={`${testId}-nudge-up`}
          aria-label={t('publishing.schedule.timeLater')}
          onClick={() => commit(shiftScheduleTime(committed || '00:00', 15))}
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <TimeColumn
          label={t('publishing.schedule.hourLabel')}
          testId={`${testId}-hours`}
          values={HOURS}
          selected={hour}
          disableBelow={min ? min.slice(0, 2) : undefined}
          selectedRef={hourButtonRef}
          listRef={hourListRef}
          onSelect={(nextHour) => {
            const nextMinute = min && nextHour === min.slice(0, 2) && minute < min.slice(3)
              ? min.slice(3)
              : minute;
            applyPart(nextHour, nextMinute);
          }}
        />
        <TimeColumn
          label={t('publishing.schedule.minuteLabel')}
          testId={`${testId}-minutes`}
          values={MINUTES}
          selected={minute}
          disableBelow={min && hour === min.slice(0, 2) ? min.slice(3) : undefined}
          selectedRef={minuteButtonRef}
          listRef={minuteListRef}
          onSelect={(nextMinute) => applyPart(hour, nextMinute)}
        />
      </div>
    </div>
  );
};

function TimeColumn({
  label,
  testId,
  values,
  selected,
  disableBelow,
  selectedRef,
  listRef,
  onSelect,
}: {
  label: string;
  testId: string;
  values: string[];
  selected: string;
  disableBelow?: string;
  selectedRef: RefObject<HTMLButtonElement>;
  listRef: RefObject<HTMLDivElement>;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div
        ref={listRef}
        data-testid={testId}
        className="h-32 overflow-y-auto rounded-md border bg-muted/20 p-0.5"
      >
        {values.map((option) => {
          const isSelected = option === selected;
          const disabled = !!disableBelow && option < disableBelow;
          return (
            <button
              key={option}
              ref={isSelected ? selectedRef : undefined}
              type="button"
              disabled={disabled}
              data-testid={`${testId}-${option}`}
              onClick={() => onSelect(option)}
              className={cn(
                'flex h-7 w-full items-center justify-center rounded-sm font-mono text-xs tabular-nums',
                isSelected && 'bg-accent text-accent-foreground',
                !isSelected && !disabled && 'hover:bg-accent/20',
                disabled && 'cursor-not-allowed opacity-30',
              )}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ScheduleTimeField;
